import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, Users, Plus, Edit2, Trash2, Key, ChevronRight, 
  Home, Shield, Phone, UserCheck, UserX, ArrowLeft, ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  getCompanies, createCompany, updateCompany, deleteCompany,
  getUsers, createUser, updateUser, deleteUser, resetUserPin,
  getCurrentUser, logout
} from '../utils/api';

export const AdminPanel = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');
  
  // Company dialogs
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', description: '' });
  
  // User dialogs
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ phone: '', pin: '', name: '', role: 'manager', company_ids: [] });
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', item: null });

  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [companiesData, usersData] = await Promise.all([
        getCompanies(),
        getUsers()
      ]);
      setCompanies(companiesData);
      setUsers(usersData);
    } catch (error) {
      toast.error('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Company handlers
  const handleSaveCompany = async () => {
    try {
      if (!companyForm.name.trim()) {
        toast.error('Company name is required');
        return;
      }
      
      if (editingCompany) {
        await updateCompany(editingCompany.id, companyForm);
        toast.success('Company updated');
      } else {
        await createCompany(companyForm);
        toast.success('Company created');
      }
      
      setShowCompanyDialog(false);
      setEditingCompany(null);
      setCompanyForm({ name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({ name: company.name, description: company.description || '' });
    setShowCompanyDialog(true);
  };

  const handleDeleteCompany = async () => {
    try {
      await deleteCompany(deleteConfirm.item.id);
      toast.success('Company deleted');
      setDeleteConfirm({ show: false, type: '', item: null });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // User handlers
  const handleSaveUser = async () => {
    try {
      if (!userForm.name.trim() || !userForm.phone.trim()) {
        toast.error('Name and phone are required');
        return;
      }
      
      if (!editingUser && (!userForm.pin || userForm.pin.length !== 4)) {
        toast.error('PIN must be 4 digits');
        return;
      }
      
      if (userForm.role === 'manager' && (!userForm.company_ids || userForm.company_ids.length === 0)) {
        toast.error('Managers must be assigned to at least one company');
        return;
      }
      
      if (editingUser) {
        const updates = {
          name: userForm.name,
          phone: userForm.phone,
          company_ids: userForm.role === 'manager' ? userForm.company_ids : [],
          is_active: userForm.is_active
        };
        await updateUser(editingUser.id, updates);
        toast.success('User updated');
      } else {
        await createUser(userForm);
        toast.success('User created');
      }
      
      setShowUserDialog(false);
      setEditingUser(null);
      setUserForm({ phone: '', pin: '', name: '', role: 'manager', company_ids: [] });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ 
      phone: user.phone, 
      pin: '', 
      name: user.name, 
      role: user.role,
      company_ids: user.company_ids || [],
      is_active: user.is_active
    });
    setShowUserDialog(true);
  };

  const toggleCompanySelection = (companyId) => {
    setUserForm(prev => {
      const current = prev.company_ids || [];
      if (current.includes(companyId)) {
        return { ...prev, company_ids: current.filter(id => id !== companyId) };
      } else {
        return { ...prev, company_ids: [...current, companyId] };
      }
    });
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(deleteConfirm.item.id);
      toast.success('User deleted');
      setDeleteConfirm({ show: false, type: '', item: null });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPin = async (user) => {
    try {
      await resetUserPin(user.id);
      toast.success(`PIN reset to 1000 for ${user.name}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="p-2 hover:bg-stone-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-stone-600" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-emerald-900 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Admin Panel
                </h1>
                <p className="text-sm text-stone-500">Manage companies and users</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/audit-logs"
                className="flex items-center gap-2 px-3 py-2 text-sm bg-stone-100 hover:bg-stone-200 rounded-lg text-stone-700"
                data-testid="audit-logs-link"
              >
                <ClipboardList className="w-4 h-4" />
                Audit Logs
              </Link>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-stone-900">{currentUser?.name}</p>
                <p className="text-xs text-emerald-600">Super Admin</p>
              </div>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                size="sm"
                className="text-stone-600"
                data-testid="logout-btn"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('companies')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'companies' 
                ? 'bg-emerald-900 text-white' 
                : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
            data-testid="tab-companies"
          >
            <Building2 className="w-4 h-4" />
            Companies ({companies.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'users' 
                ? 'bg-emerald-900 text-white' 
                : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4" />
            Users ({users.length})
          </button>
        </div>

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-stone-900">Companies</h2>
              <Button
                onClick={() => {
                  setEditingCompany(null);
                  setCompanyForm({ name: '', description: '' });
                  setShowCompanyDialog(true);
                }}
                className="bg-emerald-900 hover:bg-emerald-800 text-white"
                data-testid="add-company-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Company
              </Button>
            </div>

            <div className="grid gap-4">
              {companies.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
                  <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No companies yet. Create your first company to get started.</p>
                </div>
              ) : (
                companies.map((company) => {
                  const companyUsers = users.filter(u => 
                    u.company_ids?.includes(company.id) || u.company_id === company.id
                  );
                  return (
                    <div 
                      key={company.id} 
                      className="bg-white rounded-xl border border-stone-200 p-4 hover:shadow-md transition-shadow"
                      data-testid={`company-card-${company.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-emerald-50 rounded-lg">
                            <Building2 className="w-6 h-6 text-emerald-700" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-stone-900">{company.name}</h3>
                            {company.description && (
                              <p className="text-sm text-stone-500 mt-1">{company.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {companyUsers.length} user(s)
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                company.is_active 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {company.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCompany(company)}
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ show: true, type: 'company', item: company })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-stone-900">Users</h2>
              <Button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({ phone: '', pin: '', name: '', role: 'manager', company_ids: [] });
                  setShowUserDialog(true);
                }}
                className="bg-emerald-900 hover:bg-emerald-800 text-white"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            <div className="grid gap-4">
              {users.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
                  <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No users yet.</p>
                </div>
              ) : (
                users.map((user) => (
                  <div 
                    key={user.id} 
                    className="bg-white rounded-xl border border-stone-200 p-4 hover:shadow-md transition-shadow"
                    data-testid={`user-card-${user.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          user.role === 'super_admin' ? 'bg-purple-50' : 'bg-blue-50'
                        }`}>
                          {user.role === 'super_admin' ? (
                            <Shield className="w-6 h-6 text-purple-700" />
                          ) : (
                            <Users className="w-6 h-6 text-blue-700" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                            {user.name}
                            {!user.is_active && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                Inactive
                              </span>
                            )}
                          </h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-stone-500">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {user.phone}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'super_admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role === 'super_admin' ? 'Super Admin' : 'Manager'}
                            </span>
                          </div>
                          {user.companies && user.companies.length > 0 && (
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              <Building2 className="w-3 h-3 text-emerald-600" />
                              <span className="text-sm text-emerald-600">
                                {user.companies.map(c => c.name).join(', ')}
                              </span>
                              {user.companies.length > 1 && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full ml-1">
                                  {user.companies.length} companies
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {user.id !== currentUser?.id && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPin(user)}
                              title="Reset PIN to 1000"
                              data-testid={`reset-pin-${user.id}`}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm({ show: true, type: 'user', item: user })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-stone-400 px-2 py-1">You</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </main>

      {/* Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder="Enter company name"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={companyForm.description}
                onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                placeholder="Optional description"
                data-testid="company-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompanyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCompany}
              className="bg-emerald-900 hover:bg-emerald-800"
              data-testid="save-company-btn"
            >
              {editingCompany ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Enter user name"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value.replace(/\D/g, '') })}
                placeholder="Enter phone number"
                type="tel"
                data-testid="user-phone-input"
              />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label>Initial PIN (4 digits) *</Label>
                <Input
                  value={userForm.pin}
                  onChange={(e) => setUserForm({ ...userForm, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="1000"
                  type="password"
                  maxLength={4}
                  data-testid="user-pin-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {userForm.role === 'manager' && (
              <div className="space-y-2">
                <Label>Assign to Companies *</Label>
                <div className="border border-stone-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {companies.length === 0 ? (
                    <p className="text-sm text-stone-500">No companies available. Create a company first.</p>
                  ) : (
                    companies.map((c) => (
                      <label 
                        key={c.id} 
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={userForm.company_ids?.includes(c.id) || false}
                          onChange={() => toggleCompanySelection(c.id)}
                          className="rounded border-stone-300 text-emerald-600"
                          data-testid={`company-checkbox-${c.id}`}
                        />
                        <span className="text-sm text-stone-700">{c.name}</span>
                      </label>
                    ))
                  )}
                </div>
                {userForm.company_ids?.length > 0 && (
                  <p className="text-xs text-emerald-600">
                    {userForm.company_ids.length} company(ies) selected
                  </p>
                )}
              </div>
            )}
            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="user-active"
                  checked={userForm.is_active !== false}
                  onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                  className="rounded border-stone-300"
                  data-testid="user-active-checkbox"
                />
                <Label htmlFor="user-active">Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser}
              className="bg-emerald-900 hover:bg-emerald-800"
              data-testid="save-user-btn"
            >
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, type: '', item: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Are you sure you want to delete{' '}
            <span className="font-semibold">
              {deleteConfirm.item?.name}
            </span>
            ? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ show: false, type: '', item: null })}>
              Cancel
            </Button>
            <Button 
              onClick={deleteConfirm.type === 'company' ? handleDeleteCompany : handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
